'use client';

import { FC, useEffect, useRef, useState } from 'react';
import { cn, getIdFromNode, getTableOfContentsFromRTE } from "utils/functions";
import { fromMarkdown } from "mdast-util-from-markdown";
import GithubSlugger from "github-slugger";

interface TableOfContentProps {
  tableOfContents: any[];
  post:
    | {
        isMarkdown: true;
        body: string;
      }
    | {
        isMarkdown: false;
        body: {
          data: {};
          content: any;
          nodeType: string;
        }[];
      };
  className?: string;
}

const slugger = new GithubSlugger();

const TableContent: FC<TableOfContentProps> = ({ tableOfContents, className, post }) => {
  const [activeStep, setActiveStep] = useState(0);
  const isScrolling = useRef();
  const isTocScrolling = useRef();
  const tocRef = useRef<HTMLDivElement>(null);

  if (tableOfContents.length === 0) {
    if (!post.isMarkdown) {
      tableOfContents = getTableOfContentsFromRTE(post.body);
    } else {
      let body = fromMarkdown(post.body);
      tableOfContents = [];
      body.children.forEach((node) => {
        if (node.type === "heading" && node.depth < 4) {
          tableOfContents.push({
            // @ts-ignore
            content: [{ value: node.children[0].value }],
            // @ts-ignore
            id: slugger.slug(node.children[0].value),
            nodeType: "heading-" + node.depth,
          });
        }
      });
      slugger.reset();
    }
  }

  const handleTableContentClicked = (index: number, id: string) => {
    setActiveStep(index);
    isScrolling.current = true as any;
    isTocScrolling.current = true as any;
    const element = document.getElementById(id) as HTMLElement;
    if (!element) {
      return;
    }
    const headerOffset = 150;
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.scrollY - headerOffset;

    window.scrollTo({
      top: offsetPosition,
      behavior: "smooth",
    });
    setTimeout(() => {
      isScrolling.current = false as any;
      isTocScrolling.current = false as any;
    }, 3000);
  };

  const setTextIndent = (heading: string) => {
    switch (heading) {
      case "heading-2":
        return "ml-0";
      case "heading-3":
        return "ml-sm";
      case "heading-4":
        return "ml-md";
      default:
        return "ml-0";
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      if (isScrolling.current) {
        return;
      }
      tableOfContents?.map((item, index) => {
        const content = item?.content;
        const id = item.id ?? (getIdFromNode(content?.[0]?.value?.toString()) || "");
        if (id) {
          const headingEl = document.getElementById(id);
          const nextEl =
            tableOfContents[index + 1] &&
            document.getElementById(
              tableOfContents[index + 1].id ??
                (getIdFromNode(tableOfContents[index + 1]?.content?.[0]?.value?.toString()) || ""),
            );
          if (headingEl) {
            const headingPos = headingEl.getBoundingClientRect();
            if (headingPos.top < 200) {
              if (!nextEl) {
                setActiveStep(index);
              } else {
                const nextPos = nextEl.getBoundingClientRect();
                if (nextPos.top > 200) {
                  setActiveStep(index);
                }
              }
            }
          }
        }
        return;
      });
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [tableOfContents, post.isMarkdown]);

  useEffect(() => {
    const scrollToActiveStepInToc = (id: string) => {
      const tocActiveStepEl = document.getElementById(id + "-toc");

      if (!tocActiveStepEl || !tocRef.current || isTocScrolling.current) {
        return;
      }

      if (tocActiveStepEl.getBoundingClientRect().top + 100 > tocRef.current?.getBoundingClientRect().bottom) {
        tocRef.current?.scrollTo({
          top:
            tocActiveStepEl.offsetTop -
            tocRef.current?.offsetTop -
            tocRef.current?.clientHeight +
            tocActiveStepEl.clientHeight +
            100,
          behavior: "smooth",
        });
      }

      if (tocActiveStepEl.getBoundingClientRect().top < tocRef.current?.getBoundingClientRect().top) {
        tocRef.current?.scrollTo({
          top: tocActiveStepEl.offsetTop - tocRef.current?.offsetTop,
          behavior: "smooth",
        });
      }
    };

    if (tableOfContents?.length !== 0) {
      const content = tableOfContents?.[activeStep]?.content;
      const id = tableOfContents?.[activeStep]?.id ?? (getIdFromNode(content?.[0]?.value?.toString()) || "");
      scrollToActiveStepInToc(id);
    }
  }, [activeStep, tableOfContents]);

  return (
    <>
      {tableOfContents?.length !== 0 && (
        <div className="rounded-lg border border-primary-light-100 bg-primary-neutral-50">
          <div className="z-10 mb-sm rounded-lg bg-primary-neutral-50 px-md pb-0 pt-6 text-lg font-bold uppercase text-primary-dark-800">
            Table of contents
          </div>
          <div
            className="overflow-y-auto px-md pb-6 pt-0 text-primary-dark-500"
            ref={tocRef}
            style={{
              maxHeight: "calc(100vh - 240px)",
            }}
          >
            {tableOfContents?.map((item, index) => {
              const content = item?.content;
              const nodeType = item?.nodeType || "";
              const key = `${content?.[0]?.value}-${index}`;
              const id = item.id ?? (getIdFromNode(content?.[0]?.value?.toString()) || "");

              return (
                <button
                  key={key}
                  id={id + "-toc"}
                  onClick={() => handleTableContentClicked(index, id)}
                  className={cn(
                    "relative mb-xs block cursor-pointer text-left font-medium transition-colors",
                    `mb-0 text-sm ${
                      activeStep === index ? "text-primary-light-500" : "text-primary-dark-500"
                    } ${setTextIndent(nodeType)}`,
                  )}
                >
                  <span>
                    {content?.[0]?.value?.startsWith("[Hidden]")
                      ? content?.[0]?.value.split("[Hidden]")[1]
                      : content?.[0]?.value}
                  </span>
                  <svg
                    className={cn(
                      "absolute -left-xs top-3xs z-20 mt-5xs h-3xs w-3xs flex-shrink-0 transition-opacity",
                      activeStep === index ? "opacity-100" : "opacity-0",
                    )}
                    xmlns="http://www.w3.org/2000/svg"
                    width="9"
                    height="9"
                    viewBox="0 0 9 9"
                  >
                    <circle cx="4.5" cy="4.5" r="3.5" fill="#9973FA" stroke="#D5C6FD" strokeWidth="2" />
                  </svg>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
};

export default TableContent;
